import { Button, Card, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { PreviewAutoInvoiceParameters, PreviewAutoInvoiceReportFormat } from '@parkchirp/reporting';
import { useMemo, useState } from 'react';
import { FacilityHierarchyFilter, FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import { ReportFormProps } from './types';

type ParkingPeriodFilterValue = {
	parkingPeriodStartMonth: number | '';
	parkingPeriodStartYear: number | '';
	parkingPeriodEndMonth: number | '';
	parkingPeriodEndYear: number | '';
};

const initialParkingPeriod: ParkingPeriodFilterValue = {
	parkingPeriodStartMonth: '',
	parkingPeriodStartYear: '',
	parkingPeriodEndMonth: '',
	parkingPeriodEndYear: '',
};

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	facilities: [],
};

const monthOptions = [
	{ label: 'January', value: 0 },
	{ label: 'February', value: 1 },
	{ label: 'March', value: 2 },
	{ label: 'April', value: 3 },
	{ label: 'May', value: 4 },
	{ label: 'June', value: 5 },
	{ label: 'July', value: 6 },
	{ label: 'August', value: 7 },
	{ label: 'September', value: 8 },
	{ label: 'October', value: 9 },
	{ label: 'November', value: 10 },
	{ label: 'December', value: 11 },
];

const getYearOptions = () => {
	const startYear = 2021;
	const endYear = new Date().getFullYear() + 1;
	const yearOptions = [];

	for (let year = startYear; year <= endYear; year++) {
		yearOptions.push(year);
	}

	return yearOptions;
};

const hasParkingPeriodStart = (filter: ParkingPeriodFilterValue) => {
	return filter.parkingPeriodStartMonth !== '' && filter.parkingPeriodStartYear !== '';
};

export const PreviewAutoInvoiceModernReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [parkingPeriod, setParkingPeriod] = useState<ParkingPeriodFilterValue>(initialParkingPeriod);
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);
	const [format, setFormat] = useState<PreviewAutoInvoiceReportFormat>('csv');

	const canSubmit = useMemo(() => {
		return hasParkingPeriodStart(parkingPeriod);
	}, [parkingPeriod]);

	const reset = () => {
		setParkingPeriod(initialParkingPeriod);
		setFacilityHierarchy(initialFacilityHierarchy);
		setFormat('csv');
	};

	const updateParkingPeriod = (changes: Partial<ParkingPeriodFilterValue>) => {
		setParkingPeriod((prev) => ({ ...prev, ...changes }));
	};

	const buildParameters = (): PreviewAutoInvoiceParameters => {
		const parameters: PreviewAutoInvoiceParameters = { format };

		if (hasParkingPeriodStart(parkingPeriod)) {
			parameters.parkingPeriodStartMonth = parkingPeriod.parkingPeriodStartMonth as number;
			parameters.parkingPeriodStartYear = parkingPeriod.parkingPeriodStartYear as number;

			if (parkingPeriod.parkingPeriodEndMonth !== '' && parkingPeriod.parkingPeriodEndYear !== '') {
				parameters.parkingPeriodEndMonth = parkingPeriod.parkingPeriodEndMonth;
				parameters.parkingPeriodEndYear = parkingPeriod.parkingPeriodEndYear;
			}
		}

		applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

		if (facilityHierarchy.facilities.length === 1) {
			const facilityNumber = facilityHierarchy.facilities[0].facilityNumber;
			if (facilityNumber !== undefined && facilityNumber !== null && `${facilityNumber}` !== '') {
				parameters.facilityNumber = facilityNumber;
			}
		}

		return parameters;
	};

	const validate = () => {
		if (!hasParkingPeriodStart(parkingPeriod)) {
			onValidationError('Select a parking period start month and year.');
			return false;
		}

		if (
			(parkingPeriod.parkingPeriodEndMonth !== '' || parkingPeriod.parkingPeriodEndYear !== '') &&
			!hasParkingPeriodStart(parkingPeriod)
		) {
			onValidationError('Select a parking period start before specifying a parking period end.');
			return false;
		}

		if (parkingPeriod.parkingPeriodEndMonth !== '' && parkingPeriod.parkingPeriodEndYear === '') {
			onValidationError('Select a parking period end year.');
			return false;
		}

		if (parkingPeriod.parkingPeriodEndYear !== '' && parkingPeriod.parkingPeriodEndMonth === '') {
			onValidationError('Select a parking period end month.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'previewAutoInvoice',
			parameters: buildParameters(),
		});
	};

	return (
		<Card variant='outlined' sx={{ p: 3 }}>
			<Stack spacing={3}>
				<Stack spacing={2}>
					<Typography variant='subtitle1' fontWeight={600}>
						Parking period
					</Typography>
					<Typography variant='subtitle2' fontWeight={400}>
						Parking period start is required
					</Typography>

					<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
						<FormControl fullWidth>
							<InputLabel id='pp-start-month-label'>Start month</InputLabel>
							<Select
								labelId='pp-start-month-label'
								label='Start month'
								value={parkingPeriod.parkingPeriodStartMonth}
								onChange={(e) =>
									updateParkingPeriod({ parkingPeriodStartMonth: e.target.value as number | '' })
								}
							>
								<MenuItem value=''>
									<em>None</em>
								</MenuItem>
								{monthOptions.map((month) => (
									<MenuItem key={month.value} value={month.value}>
										{month.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl fullWidth>
							<InputLabel id='pp-start-year-label'>Start year</InputLabel>
							<Select
								labelId='pp-start-year-label'
								label='Start year'
								value={parkingPeriod.parkingPeriodStartYear}
								onChange={(e) =>
									updateParkingPeriod({ parkingPeriodStartYear: e.target.value as number | '' })
								}
							>
								<MenuItem value=''>
									<em>None</em>
								</MenuItem>
								{getYearOptions().map((year) => (
									<MenuItem key={year} value={year}>
										{year}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>

					<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
						<FormControl fullWidth>
							<InputLabel id='pp-end-month-label'>End month</InputLabel>
							<Select
								labelId='pp-end-month-label'
								label='End month'
								value={parkingPeriod.parkingPeriodEndMonth}
								onChange={(e) =>
									updateParkingPeriod({ parkingPeriodEndMonth: e.target.value as number | '' })
								}
							>
								<MenuItem value=''>
									<em>None</em>
								</MenuItem>
								{monthOptions.map((month) => (
									<MenuItem key={month.value} value={month.value}>
										{month.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl fullWidth>
							<InputLabel id='pp-end-year-label'>End year</InputLabel>
							<Select
								labelId='pp-end-year-label'
								label='End year'
								value={parkingPeriod.parkingPeriodEndYear}
								onChange={(e) =>
									updateParkingPeriod({ parkingPeriodEndYear: e.target.value as number | '' })
								}
							>
								<MenuItem value=''>
									<em>None</em>
								</MenuItem>
								{getYearOptions().map((year) => (
									<MenuItem key={year} value={year}>
										{year}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
				</Stack>

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Output
					</Typography>

					<ToggleButtonGroup
						exclusive
						size='small'
						value={format}
						onChange={(_, nextFormat: PreviewAutoInvoiceReportFormat | null) => {
							if (nextFormat) {
								setFormat(nextFormat);
							}
						}}
					>
						<ToggleButton value='csv'>CSV</ToggleButton>
						<ToggleButton value='pdf'>PDF</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				<Divider />

				<Stack direction='row' justifyContent='flex-end' spacing={2}>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
